import React from 'react';
import { psychologistInfo } from '../mock/mockData';
import { Mail, Instagram, Calendar, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';

const ContactSection = () => {
  const handleBookingClick = () => {
    window.open(psychologistInfo.bookingLink, '_blank');
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-rose-50 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 left-10 w-96 h-96 bg-rose-200 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gold-200 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-rose-900 mb-4">
            Marcar Consulta
          </h2>
          <p className="text-lg text-gray-600">
            Dê o primeiro passo para o seu bem-estar. Entre em contacto e agende a sua consulta.
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div className="bg-white rounded-3xl p-8 border border-rose-200 shadow-lg">
                <h3 className="text-2xl font-bold text-rose-800 mb-6">
                  Informações de Contacto
                </h3>

                <div className="space-y-6">
                  {/* Email */}
                  <div className="flex items-start gap-4 group">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-rose-100 to-gold-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Mail className="h-6 w-6 text-rose-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Email</p>
                      <a
                        href={`mailto:${psychologistInfo.email}`}
                        className="text-rose-600 hover:text-rose-500 font-medium transition-colors"
                      >
                        {psychologistInfo.email}
                      </a>
                    </div>
                  </div>

                  {/* Instagram */}
                  <div className="flex items-start gap-4 group">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-rose-100 to-gold-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Instagram className="h-6 w-6 text-rose-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Instagram</p>
                      <a
                        href={`https://instagram.com/${psychologistInfo.instagram}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-rose-600 hover:text-rose-500 font-medium transition-colors"
                      >
                        @{psychologistInfo.instagram}
                      </a>
                    </div>
                  </div>

                  {/* Consultation Type */}
                  <div className="flex items-start gap-4 group">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-rose-100 to-gold-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Calendar className="h-6 w-6 text-rose-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Modalidade</p>
                      <p className="text-rose-800 font-medium">
                        {psychologistInfo.consultationType}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* About Info */}
              <div className="bg-gradient-to-br from-rose-50 to-white rounded-3xl p-8 border border-rose-200">
                <h3 className="text-xl font-bold text-rose-800 mb-4">
                  Sobre
                </h3>
                <p className="text-gray-700 leading-relaxed text-sm">
                  {psychologistInfo.bio}
                </p>
              </div>
            </div>

            {/* Booking Card */}
            <div className="bg-white rounded-3xl p-8 border border-rose-200 shadow-lg flex flex-col justify-center items-center text-center">
              <div className="mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-rose-100 to-gold-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Calendar className="h-10 w-10 text-rose-600" />
                </div>
                <h3 className="text-3xl font-bold text-rose-800 mb-4">
                  Pronta para começar?
                </h3>
                <p className="text-gray-600 leading-relaxed mb-8 max-w-md">
                  Clique no botão abaixo para preencher o formulário de marcação de consulta. 
                  Entrarei em contacto consigo nas próximas 24-48 horas para confirmar a sua marcação.
                </p>
              </div>

              <Button
                onClick={handleBookingClick}
                size="lg"
                className="w-full max-w-md bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                Preencher Formulário de Marcação
                <ExternalLink className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>

              <p className="text-xs text-gray-500 mt-6">
                O formulário abrirá numa nova janela
              </p>

              <div className="mt-8 pt-8 border-t border-rose-200 w-full">
                <p className="text-sm text-gray-600">
                  Também pode entrar em contacto através de:
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mt-4 justify-center">
                  <a
                    href={`mailto:${psychologistInfo.email}`}
                    className="inline-flex items-center gap-2 text-rose-600 hover:text-rose-700 font-medium transition-colors text-sm"
                  >
                    <Mail className="h-4 w-4" />
                    Email
                  </a>
                  <a
                    href={`https://instagram.com/${psychologistInfo.instagram}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-rose-600 hover:text-rose-700 font-medium transition-colors text-sm"
                  >
                    <Instagram className="h-4 w-4" />
                    Instagram
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
